/**
 * LE JUGEMENT D'UN TRACÉ DU MANUEL — la nature de réponse `trace` (chantier
 * « manuel vivant », Φ-MV-8 ; analyse 05, L-2 et L-3), sorti du serveur le
 * 18 octobre 2026 (chantier « Élève », lot 8, palier L0 : « voir avant de
 * produire », AB#943).
 *
 * ─── Pourquoi ici ───────────────────────────────────────────────────────────
 *
 * Il vivait dans `correction-trace.ts` (API). La console de relecture en a
 * besoin à son tour : elle rejoue chaque TRACÉ FAUTIF prévu pour prouver qu'il
 * déclenche bien son erreur, et elle ouvre un BAC À SABLE où le relecteur
 * trace comme un élève et lit le verdict que l'élève recevrait. Réécrit dans
 * la console, ce serait deux vérités — et la relecture dirait « déclenche »
 * d'un tracé que le serveur jugerait autrement. Il est donc ici, PUR, et le
 * serveur comme la console l'appellent. Aucun modèle (invariant I-11 de la
 * console ; décision 5 du fondateur, 17 octobre : sur une figure, le code juge).
 *
 * ─── L'ordre des questions (inchangé) ───────────────────────────────────────
 *
 *  1. la construction tient-elle TOUTES les contraintes attendues ? Juste ;
 *  2. sinon, est-ce un TRACÉ FAUTIF PRÉVU — tient-elle toutes les contraintes
 *     de l'un d'eux ? L'erreur a alors un nom, celui de la réponse fautive de
 *     même libellé, et le verdict est « à revoir » ;
 *  3. sinon, la PREMIÈRE contrainte manquée qui déclare une erreur la nomme, et
 *     le verdict est celui que le vérificateur impose (§ 5.4) : `partiel` si
 *     une partie des contraintes tient, `erreur` si aucune — et `erreur`
 *     toujours quand c'est une contrainte `image` qui manque (tranche T17).
 *
 * Atelier de figures, L3 (F3) — CONSTRUIRE, deux ajouts ADDITIFS :
 *
 *  - 1 bis. tout tient, mais avec un outil que la consigne EXCLUAIT
 *    (`methode.outilsExclus`, arbitrage A2) : « partiel », et la phrase le dit
 *    (« Construite avec l'outil Médiatrice, la consigne demandait le compas. ») ;
 *  - les ARCS QUI NE SE COUPENT PAS (`erreursDeConstruction.arcsDisjoints`) :
 *    l'erreur est nommée dès que la construction n'est pas juste — même quand
 *    la cible manque, puisque c'est l'ouverture du compas qui l'a empêchée.
 *
 * Le repère de départ est celui de la BANQUE — la figure d'énoncé, ou le repère
 * que le corrigé déclare : du document de l'élève, seuls ses objets sont lus.
 */
import { attenduDuTrace, documentDeTrace, repereDeTrace, } from '../types/manuel-exercices.js';
import { griefsDocument, resoudreConstruction, } from './construction.js';
import { normaliserFigureV2 } from './figure-v2.js';
import { arcsQuiNeSeCoupentPas, phraseDeMethode, PHRASE_ARCS_DISJOINTS, } from './outils-construction.js';
import { toleranceDe, verifierConstruction } from './verifier.js';
/** Ce que l'élève lit, mot pour mot, selon le jugement. */
export const RETROACTIONS_TRACE = {
    manque: 'Il manque ton tracé.',
    illisible: 'Ton tracé n’a pas pu être lu. Recommence-le.',
    sansDroite: 'Il manque la droite. Relie deux points avec l’outil Droite.',
    sansPoint: 'Il manque ton point. Place-le avec l’outil Point.',
    /* `CAP-07`, lot 1 — ce qui manque se dit par son nom, avec l'outil qui le trace. */
    sansCercle: 'Il manque le cercle. Trace-le avec l’outil Cercle.',
    sansPolygone: 'Il manque le polygone. Trace-le avec l’outil Polygone, sommet après sommet.',
    sansCourbe: 'Il manque la courbe. Trace-la avec l’outil Fonction.',
    sansDemiPlan: 'Il manque le demi-plan. Trace sa frontière, puis hachure le bon côté avec l’outil Demi-plan.',
    sansVecteur: 'Il manque le vecteur. Trace-le avec l’outil Vecteur.',
    /** L'exercice n'a ni figure d'énoncé ni repère : il ne se corrige pas. */
    sansRepere: 'Cet exercice ne se corrige pas encore.',
    juste: 'C’est juste.',
    partiel: 'Une partie de ton tracé est juste. Compare-le avec le tracé attendu.',
    aRevoir: 'Ton tracé n’est pas encore le bon. Compare-le avec le tracé attendu.',
    /** L3 (F3) : tout tient, mais pas avec la méthode que la consigne impose (la phrase précise suit). */
    methode: 'Ta figure est juste, mais elle n’est pas construite comme la consigne le demande.',
};
/** Ce qu'on dit quand l'objet demandé n'est pas tracé, par genre de cible. */
const MANQUE_PAR_GENRE = {
    point: RETROACTIONS_TRACE.sansPoint,
    ligne: RETROACTIONS_TRACE.sansDroite,
    droite: RETROACTIONS_TRACE.sansDroite,
    segment: RETROACTIONS_TRACE.sansDroite,
    cercle: RETROACTIONS_TRACE.sansCercle,
    polygone: RETROACTIONS_TRACE.sansPolygone,
    fonction: RETROACTIONS_TRACE.sansCourbe,
    /* Tranche T42 : une fonction définie par parties se trace partie par partie, à l'outil Fonction. */
    par_parties: RETROACTIONS_TRACE.sansCourbe,
    demi_plan: RETROACTIONS_TRACE.sansDemiPlan,
    vecteur: RETROACTIONS_TRACE.sansVecteur,
    /* L6 (F8) : une courbe esquissée d'un geste. */
    esquisse: 'Il manque ta courbe. Esquisse-la d’un geste.',
};
/**
 * La phrase de ce qui manque. La règle d'origine (Φ-MV-8) reste, à la lettre :
 * un exercice qui ne demande que des points dit « ton point », tout autre qui
 * ne demande que des points, des droites et des segments dit « la droite ». Dès
 * qu'une cible est d'un genre ouvert par `CAP-07`, c'est la PREMIÈRE cible sans
 * objet, dans l'ordre du corrigé, qui parle.
 */
export function ceQuiManqueAuTrace(cibles, liaisons) {
    const dOrigine = (g) => g === 'point' || g === 'ligne' || g === 'droite' || g === 'segment';
    if (cibles.every((c) => dOrigine(c.genre))) {
        return cibles.every((c) => c.genre === 'point')
            ? RETROACTIONS_TRACE.sansPoint
            : RETROACTIONS_TRACE.sansDroite;
    }
    const manquante = cibles.find((c) => liaisons[c.id] === null);
    return manquante === undefined ? RETROACTIONS_TRACE.manque : MANQUE_PAR_GENRE[manquante.genre];
}
/** La figure d'énoncé d'un exercice de tracé (`{ genre, decrit, figure }`), normalisée ; `null` : aucune, ou pas un plan. */
export function enonceDeTrace(figure) {
    const n = normaliserFigureV2(figure?.figure);
    return 'figure' in n && n.figure.espace === 'plan' ? n.figure : null;
}
/** Juge une construction (ses seuls objets). Pur : ni modèle, ni horloge, ni base. */
export function jugerTrace(exercice, objetsRecus) {
    const { corrige } = exercice;
    const enonce = enonceDeTrace(exercice.figure);
    const repere = repereDeTrace(corrige, enonce);
    if (repere === null)
        return { genre: 'incomplet', retroaction: RETROACTIONS_TRACE.sansRepere };
    const calque = enonce !== null;
    const objets = Array.isArray(objetsRecus) ? objetsRecus : [];
    if (objets.length === 0)
        return { genre: 'incomplet', retroaction: RETROACTIONS_TRACE.manque };
    /* Le repère et le calque sont ceux de la banque : l'application n'envoie que ses objets. */
    const eleve = documentDeTrace(repere, { objets, calque });
    if (griefsDocument(eleve, enonce).length > 0) {
        return { genre: 'incomplet', retroaction: RETROACTIONS_TRACE.illisible };
    }
    const attendu = documentDeTrace(repere, {
        objets: corrige.solution,
        calque,
    });
    const verification = verifierConstruction(eleve, enonce, attenduDuTrace(corrige, attendu));
    /* L3 (F3) : la méthode imposée — dite, jamais une contrainte manquée à elle seule. */
    const methode = phraseDeMethode(verification.methode, corrige.methode?.outilDemande ?? null);
    const juge = (verdict, retroaction, erreurCode = null, fautive = null) => ({
        genre: 'juge',
        verdict,
        retroaction,
        erreurCode,
        fautive,
        eleve,
        attendu,
        enonce,
        verification,
        methode,
    });
    /* L3 (F3) : LES ARCS QUI NE SE COUPENT PAS — l'ouverture du compas est trop
       petite. Quand le contenu déclare cette erreur, elle est nommée dès que la
       construction n'est pas juste — même quand la cible manque, puisque c'est
       elle qui l'a empêchée d'exister. Sans déclaration, rien ne change. */
    const aliasArcs = corrige.erreursDeConstruction?.arcsDisjoints;
    const arcsDisjoints = () => {
        if (aliasArcs === undefined)
            return false;
        const resolue = resoudreConstruction(eleve, enonce);
        return arcsQuiNeSeCoupentPas(eleve, resolue, toleranceDe(resolue.repere, undefined).distance);
    };
    if (Object.values(verification.liaisons).some((o) => o === null)) {
        if (arcsDisjoints()) {
            return juge('erreur', PHRASE_ARCS_DISJOINTS, aliasArcs ?? null);
        }
        return {
            genre: 'incomplet',
            retroaction: ceQuiManqueAuTrace(corrige.cibles, verification.liaisons),
        };
    }
    /* L6 (F8) : une esquisse qu'on n'a pas su LIRE n'est pas jugée — ni verdict ni
       essai (SPEC § 7.6) : la phrase dit ce qui empêche la lecture. */
    const illisible = verification.constats.find((c) => c.type === 'caracteristiques' && c.etat === 'indeterminee');
    if (illisible !== undefined)
        return { genre: 'incomplet', retroaction: illisible.texte };
    /* 1. Toutes les contraintes tiennent. Une écriture à revoir (`a_ecrire`) vaut le point entier. */
    if (verification.verdict === 'juste' || verification.verdict === 'a_ecrire') {
        return juge('juste', RETROACTIONS_TRACE.juste);
    }
    /* 1 bis (L3). Tout tient, mais avec un outil que la consigne excluait : le
       vérificateur plafonne à « partiel » (arbitrage A2) et la phrase le DIT. */
    const toutTient = verification.constats.every((c) => c.forme || c.etat === 'tenue');
    if (methode !== null && toutTient) {
        return juge('partiel', RETROACTIONS_TRACE.methode);
    }
    /* 2. Un tracé fautif prévu : l'erreur a un nom. */
    for (const t of corrige.tracesFautifs ?? []) {
        const fautive = exercice.fautives.find((f) => f.reponse === t.reponse);
        if (fautive === undefined)
            continue;
        const v = verifierConstruction(eleve, enonce, attenduDuTrace({ cibles: corrige.cibles, contraintes: t.contraintes }, attendu));
        if (v.verdict === 'juste') {
            return juge('erreur', RETROACTIONS_TRACE.aRevoir, fautive.erreurCode, fautive);
        }
    }
    /* 3. La première contrainte manquée qui déclare une erreur la nomme.
  
       UN TRACÉ QUI N'EST PAS L'IMAGE EST UNE ERREUR (tranche T17). « Trace l'image
       du triangle » : le triangle de départ redessiné, le triangle glissé au lieu
       d'être retourné, le quart de tour dans l'autre sens ont la bonne aire — et
       ne sont pas, même en partie, la réponse. Dès qu'une contrainte `image`
       n'est pas tenue, les autres ne rachètent rien : jamais « partiel ». */
    /* L3 : des arcs qui ne se coupent pas, la cible tracée quand même (à main levée). */
    if (arcsDisjoints()) {
        return juge('erreur', PHRASE_ARCS_DISJOINTS, aliasArcs ?? null);
    }
    const pasLImage = verification.constats.some((c) => c.type === 'image' && c.etat !== 'tenue');
    const verdict = verification.verdict === 'partiel' && !pasLImage ? 'partiel' : 'erreur';
    const retroaction = verdict === 'partiel' ? RETROACTIONS_TRACE.partiel : RETROACTIONS_TRACE.aRevoir;
    const manquee = verification.constats.find((c) => c.etat !== 'tenue' && corrige.contraintes[c.contrainte]?.erreur !== undefined);
    const code = manquee === undefined ? undefined : corrige.contraintes[manquee.contrainte]?.erreur;
    return juge(verdict, retroaction, code ?? null);
}
//# sourceMappingURL=juger-trace.js.map